import { z } from 'zod'

/**
 * The normalised internal event shape (SW-401).
 *
 * Shopify's payloads are the only thing that knows about `inventory_item_id`, GIDs, or
 * per-location rows. Everything downstream; the DO, the decision logic, reconciliation,
 * speaks this shape, which is what keeps `core/` free of platform knowledge and makes the
 * reconciliation sweep able to reuse the identical decision path (SW-405).
 */

export const inventoryEventSchema = z.object({
  shopDomain: z.string(),
  variantId: z.string(),
  productId: z.string(),
  /** Quantity at this location only. The DO aggregates across locations. */
  locationId: z.string().nullable(),
  available: z.number().int(),
  /** 'continue' makes a variant purchasable at zero quantity, a restock with no movement. */
  inventoryPolicy: z.enum(['deny', 'continue']).default('deny'),
  productStatus: z.enum(['active', 'archived', 'draft']).default('active'),
  occurredAt: z.number().int(),
  /** webhook | reconciliation | manual, carried through to `restock_events.source`. */
  source: z.enum(['webhook', 'reconciliation', 'manual', 'policy_change']).default('webhook'),
  webhookId: z.string().nullable().default(null),
})

export type InventoryEvent = z.infer<typeof inventoryEventSchema>

/** The message the notifications queue carries. Thin by design, see SW-404. */
export const notificationJobSchema = z.object({
  v: z.literal(1),
  shopId: z.string(),
  shopDomain: z.string(),
  subscriptionId: z.string(),
  restockEventId: z.string(),
  deliveryId: z.string(),
  channel: z.enum(['email', 'sms', 'whatsapp']),
  variantId: z.string(),
  /** Present only so the consumer can order fairly; never used to render. */
  enqueuedAt: z.number().int(),
})

export type NotificationJob = z.infer<typeof notificationJobSchema>

/**
 * Nothing rendered is put on the queue. Passing IDs means a template fix applies to every
 * message still queued, and it keeps messages three orders of magnitude clear of the 128 KB
 * ceiling, a 50,000-subscriber fan-out is 500 `sendBatch` calls of pointers, not payloads.
 */
export function encodeJob(job: NotificationJob): NotificationJob {
  return notificationJobSchema.parse(job)
}
